
import { useState, useEffect } from 'react';
import { Button } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { BookOpen, Users, AlertTriangle, Zap } from 'lucide-react';
import { TeacherSubjectsManager } from './TeacherSubjectsManager';
import { SpecializedTutoringManager } from './SpecializedTutoringManager';
import { PrimaryLevelChangeNoticeModal } from './components/PrimaryLevelChangeNoticeModal';
import { QuickAddPrimarySubjectsModal } from './components/QuickAddPrimarySubjectsModal';
import { useTeacherSubjects } from '@/hooks/useTeacherSubjects';

interface SubjectVerificationStepProps {
  teacherCode: string;
  onComplete: () => void;
  onBack: () => void;
}

export function SubjectVerificationStep({ teacherCode, onComplete, onBack }: SubjectVerificationStepProps) {
  const [stepConfirmed, setStepConfirmed] = useState(false);
  const [showPrimaryNotice, setShowPrimaryNotice] = useState(true);
  const [showQuickAddModal, setShowQuickAddModal] = useState(false);
  const [refreshKey, setRefreshKey] = useState(0);
  const { subjects, addMultipleSubjects } = useTeacherSubjects(teacherCode);

  // Filter subjects with old "ประถม" grade level
  const primarySubjects = subjects.filter(s => s.grade_level === "ประถม");
  const hasPrimarySubjects = primarySubjects.length > 0;
  const shouldShowNotice = hasPrimarySubjects && showPrimaryNotice;

  const handleComplete = () => {
    if (stepConfirmed) {
      onComplete();
    }
  };

  return (
    <div className="space-y-6">
      <PrimaryLevelChangeNoticeModal
        open={shouldShowNotice}
        onClose={() => setShowPrimaryNotice(false)}
        primarySubjects={primarySubjects}
        onQuickAdd={() => setShowQuickAddModal(true)}
      />

      <QuickAddPrimarySubjectsModal
        open={showQuickAddModal}
        onClose={() => setShowQuickAddModal(false)}
        primarySubjects={primarySubjects}
        existingSubjects={subjects}
        onAddSubjects={async (subjectsData) => {
          const success = await addMultipleSubjects(subjectsData);
          if (success) {
            setRefreshKey(prev => prev + 1);
          }
          return success;
        }}
        teacherCode={teacherCode}
      />
      <div className="text-center">
        <Badge variant="outline" className="mb-4">
          ขั้นตอนที่ 2: ยืนยันวิชาที่สอน
        </Badge>
      </div>

      {/* Warning for deprecated primary subjects */}
      {hasPrimarySubjects && (
        <Alert className="bg-red-50 border-red-300">
          <AlertTriangle className="h-4 w-4 text-red-600" />
          <AlertDescription className="text-red-800">
            <div className="flex items-center justify-between gap-4">
              <div>
                <p className="font-medium">⚠️ พบวิชาระดับ "ประถม" ที่ต้องจัดการ ({primarySubjects.length} วิชา)</p>
                <p className="text-sm">ระดับ "ประถม" ถูกแบ่งเป็น "ประถมต้น" และ "ประถมปลาย" แล้ว กรุณาเพิ่มวิชาใหม่</p>
              </div>
              <Button
                onClick={() => setShowQuickAddModal(true)}
                className="bg-orange-600 hover:bg-orange-700 whitespace-nowrap"
              >
                <Zap className="w-4 h-4 mr-2" />
                เพิ่มวิชาใหม่อย่างรวดเร็ว
              </Button>
            </div>
          </AlertDescription>
        </Alert>
      )}

      {/* Encouraging Message */}
      <Alert className="bg-blue-50 border-blue-200">
        <Users className="h-4 w-4 text-blue-600" />
        <AlertDescription className="text-blue-800">
          <div className="space-y-2">
            <p className="font-medium">💡 เชิญชวนให้ครูอัพเดทข้อมูลให้เป็นปัจจุบัน</p>
            <p className="text-sm">
              การกรอกข้อมูลวิชาที่สอนและการติวสอบพิเศษให้ครบถ้วนและเป็นปัจจุบัน 
              จะช่วยให้ทางสถาบันสามารถจัดหานักเรียนให้กับท่านได้มากขึ้น 
              และเหมาะสมกับความเชี่ยวชาญของท่าน
            </p>
            <p className="text-sm font-medium text-blue-700">
              🎯 ยิ่งข้อมูลครบถ้วน ยิ่งได้โอกาสสอนมากขึ้น!
            </p>
          </div>
        </AlertDescription>
      </Alert>

      {/* วิชาที่สอน */}
      <div>
        <h4 className="text-md font-semibold text-amber-800 mb-4 flex items-center gap-2">
          <BookOpen className="w-5 h-5" />
          วิชาที่สอน
        </h4>
        <TeacherSubjectsManager key={refreshKey} teacherCode={teacherCode} />
      </div>

      {/* การติวสอบพิเศษ */}
      <div>
        <h4 className="text-md font-semibold text-amber-800 mb-4 flex items-center gap-2">
          <Users className="w-5 h-5" />
          การติวสอบพิเศษ
        </h4>
        <SpecializedTutoringManager teacherCode={teacherCode} />
      </div>

      {/* Confirmation Checkbox */}
      <div className="flex items-center space-x-2 p-4 bg-orange-50 border border-orange-200 rounded-lg">
        <Checkbox
          id="step2Confirmed"
          checked={stepConfirmed}
          onCheckedChange={(checked) => setStepConfirmed(checked as boolean)}
        />
        <Label htmlFor="step2Confirmed" className="text-sm font-medium text-orange-800">
          ข้าพเจ้าได้ตรวจสอบข้อมูลวิชาที่สอนและการติวสอบพิเศษข้างต้นเรียบร้อยแล้ว และยืนยันว่าข้อมูลถูกต้องครบถ้วน *
        </Label>
      </div>

      {/* Navigation */}
      <div className="flex justify-between">
        <Button
          variant="outline"
          onClick={onBack}
        >
          กลับขั้นตอนก่อนหน้า
        </Button>
        <Button
          onClick={handleComplete}
          disabled={!stepConfirmed}
          className="bg-orange-600 hover:bg-orange-700"
        >
          ไปขั้นตอนถัดไป
        </Button>
      </div>
    </div>
  );
}
